/**
 * Evaluates a simple arithmetic expression string.
 * Supports: +, -, *, / and decimal numbers.
 * Returns the numeric result rounded to 2 decimal places,
 * or null if the expression is invalid.
 *
 * Security: only allows digits, operators, dots, spaces.
 * No eval() — uses a safe tokeniser/parser.
 */
export function evalArithmetic(input) {
  if (input === null || input === undefined) return null
  const str = String(input).trim()
  if (str === '') return null

  // If it's already a plain number (with optional leading minus), return it
  if (/^-?\d+(\.\d+)?$/.test(str)) return parseFloat(str)

  // Only allow safe characters: digits, operators, decimals, spaces
  if (!/^[\d\s+\-*/.]+$/.test(str)) return null

  // Must contain at least one operator (otherwise it's just a number)
  if (!/[+\-*/]/.test(str)) {
    const num = parseFloat(str)
    return isNaN(num) ? null : num
  }

  try {
    const result = computeExpression(str)
    if (isNaN(result) || !isFinite(result)) return null
    return Math.round(result * 100) / 100
  } catch {
    return null
  }
}

function computeExpression(str) {
  // Tokenise into numbers and operators
  const tokens = str.match(/(\d+\.?\d*|[+\-*/])/g)
  if (!tokens) return NaN

  const nums = []
  const ops = []
  for (const token of tokens) {
    if (/^[+\-*/]$/.test(token)) {
      ops.push(token)
    } else {
      nums.push(parseFloat(token))
    }
  }
  if (nums.length !== ops.length + 1) return NaN

  // First pass: handle * and / (left-to-right)
  let i = 0
  while (i < ops.length) {
    if (ops[i] === '*' || ops[i] === '/') {
      const result = ops[i] === '*'
        ? nums[i] * nums[i + 1]
        : nums[i] / nums[i + 1]
      nums.splice(i, 2, result)
      ops.splice(i, 1)
    } else {
      i++
    }
  }

  // Second pass: handle + and - (left-to-right)
  let total = nums[0]
  for (let j = 0; j < ops.length; j++) {
    if (ops[j] === '+') total += nums[j + 1]
    if (ops[j] === '-') total -= nums[j + 1]
  }
  return total
}

/**
 * True when a string contains a recognised arithmetic operator.
 * Used by the input component to show a "this is an expression" affordance.
 */
export function looksLikeExpression(str) {
  if (str === null || str === undefined) return false
  return /[+\-*/]/.test(String(str))
}
